/**
 * /superadmin/tenants/{id} — the Tenant 360.
 *
 * Maps to GET /api/v1/admin/tenants/{id} (tenant360Response): header row,
 * resolved features (plan ∪ overrides), raw feature_overrides, employee
 * count, role breakdown, last ~20 audit events.
 *
 * The headline capability: the FEATURE-OVERRIDES EDITOR — a catalog
 * checklist (GET /admin/features/catalog) that support toggles to grant
 * ONE customer a module (e.g. time_tracking) without touching its plan,
 * then PUTs /admin/tenants/{id}/feature-overrides. The response carries
 * the freshly-resolved set, which we write straight back into the cache
 * so the "Resolved features" panel updates without a refetch.
 *
 * Resolved features are READ-ONLY (plan ∪ overrides, computed server-
 * side). Overrides are the editable lever. A feature that is ON via the
 * plan still shows in resolved even if its override box is unchecked —
 * the editor only governs the override layer, never the plan grant.
 */

import { useEffect, useMemo, useState } from "react";
import {
  createFileRoute,
  Link,
  useParams,
} from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowLeft,
  Check,
  Loader2,
  RotateCcw,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { adminConsoleApi, toApiError } from "@/lib/api";
import type {
  AdminTenant360,
  UpdateOverridesResponse,
} from "@/types/admin-console";
import { PageHeader } from "@/components/shared/page-header";
import { StatusBadge } from "@/components/shared/status-badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/lib/auth";
import type { SignupAttribution } from "@/lib/attribution";

export const Route = createFileRoute("/_authgated/superadmin/tenants/$id")({
  component: TenantDetailRoute,
});

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function TenantDetailRoute() {
  const { id } = useParams({
    from: "/_authgated/superadmin/tenants/$id",
  });
  const tenantId = Number(id);
  const queryClient = useQueryClient();

  const tenantKey = ["admin-tenant", tenantId] as const;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: tenantKey,
    queryFn: ({ signal }) =>
      adminConsoleApi.getTenant(tenantId, signal),
    enabled: Number.isFinite(tenantId) && tenantId > 0,
  });

  const catalogQuery = useQuery({
    queryKey: ["admin-feature-catalog"],
    queryFn: ({ signal }) => adminConsoleApi.featureCatalog(signal),
    staleTime: 5 * 60 * 1000,
  });

  if (!Number.isFinite(tenantId) || tenantId <= 0) {
    return (
      <>
        <BackLink />
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Invalid tenant</AlertTitle>
          <AlertDescription>
            The tenant id in the URL isn&apos;t a number.
          </AlertDescription>
        </Alert>
      </>
    );
  }

  if (error) {
    return (
      <>
        <BackLink />
        <div className="flex flex-col items-start gap-3">
          <Alert variant="destructive" className="max-w-md">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Couldn&apos;t load tenant</AlertTitle>
            <AlertDescription>
              {toApiError(error).message}
            </AlertDescription>
          </Alert>
          <Button variant="outline" onClick={() => refetch()}>
            <RotateCcw className="h-4 w-4" />
            Retry
          </Button>
        </div>
      </>
    );
  }

  if (isLoading || !data) {
    return (
      <>
        <BackLink />
        <div className="space-y-4">
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </>
    );
  }

  const t = data.tenant;

  return (
    <>
      <BackLink />
      <PageHeader
        title={t.name}
        description={`Slug: ${t.slug}`}
        actions={<StatusBadge status={t.subscription_status} />}
      />

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Header facts */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <dt className="text-muted-foreground">Tenant ID</dt>
              <dd className="tabular-nums">{t.id}</dd>
              <dt className="text-muted-foreground">Plan</dt>
              <dd>{t.plan_name ?? "—"}</dd>
              <dt className="text-muted-foreground">Plan Code</dt>
              <dd className="font-mono text-xs">
                {t.plan_code ?? "—"}
              </dd>
              <dt className="text-muted-foreground">Employees</dt>
              <dd className="tabular-nums">{data.employee_count}</dd>
              <dt className="text-muted-foreground">Active</dt>
              <dd>{t.is_active ? "Yes" : "No"}</dd>
              <dt className="text-muted-foreground">Created</dt>
              <dd>{fmtDateTime(t.created_at)}</dd>
              <dt className="text-muted-foreground">Expires</dt>
              <dd>
                {t.subscription_expires_at
                  ? fmtDateTime(t.subscription_expires_at)
                  : "—"}
              </dd>
            </dl>

            <Separator className="my-4" />

            <p className="mb-2 text-sm font-medium">Role breakdown</p>
            {data.role_breakdown.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No members yet.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {data.role_breakdown.map((rb) => (
                  <li
                    key={rb.role}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="flex items-center gap-2">
                      <Users className="h-3.5 w-3.5 text-muted-foreground" />
                      {rb.role}
                    </span>
                    <span className="tabular-nums text-muted-foreground">
                      {rb.count}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Features */}
        <div className="space-y-4 lg:col-span-2">
          <ResolvedFeaturesCard resolved={data.resolved_features} />
          <FeatureOverridesEditor
            tenantId={tenantId}
            overrides={data.feature_overrides}
            catalog={catalogQuery.data?.items ?? []}
            catalogLoading={catalogQuery.isLoading}
            onSaved={(res) => {
              // Write the freshly-resolved set back into the 360 cache so
              // the Resolved-features panel updates with no refetch.
              queryClient.setQueryData<AdminTenant360>(tenantKey, (prev) =>
                prev
                  ? {
                      ...prev,
                      feature_overrides: res.feature_overrides,
                      resolved_features: res.resolved_features,
                    }
                  : prev,
              );
            }}
          />
        </div>
      </div>

      <SignupSourceCard
        tenantId={tenantId}
        isInternal={data.is_internal}
        attribution={data.signup_attribution}
        onSaved={(isInternal) =>
          queryClient.setQueryData<AdminTenant360>(tenantKey, (prev) =>
            prev ? { ...prev, is_internal: isInternal } : prev,
          )
        }
      />

      {/* Recent audit */}
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-base">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {data.recent_audit.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No recorded activity for this tenant yet.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {data.recent_audit.map((a) => (
                <li
                  key={a.id}
                  className="flex items-center justify-between gap-3 py-2.5 text-sm"
                >
                  <div className="min-w-0">
                    <span className="font-medium">{a.action}</span>{" "}
                    <span className="text-muted-foreground">
                      {a.resource_type}
                      {a.resource_id ? ` #${a.resource_id}` : ""}
                    </span>
                    <p className="truncate text-xs text-muted-foreground">
                      {a.actor_email ?? "system"}
                    </p>
                  </div>
                  <span className="whitespace-nowrap text-xs text-muted-foreground">
                    {fmtDateTime(a.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function BackLink() {
  return (
    <Link
      to="/superadmin/tenants"
      className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      All tenants
    </Link>
  );
}

function ResolvedFeaturesCard({ resolved }: { resolved: string[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Resolved Features</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-sm text-muted-foreground">
          The net feature set this tenant sees (plan grant ∪ overrides).
          Computed server-side — read-only.
        </p>
        {resolved.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No features enabled.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {resolved.map((f) => (
              <Badge
                key={f}
                variant="outline"
                className="border-transparent bg-accent text-accent-foreground"
              >
                <Check className="mr-1 h-3 w-3" />
                {f}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function FeatureOverridesEditor({
  tenantId,
  overrides,
  catalog,
  catalogLoading,
  onSaved,
}: {
  tenantId: number;
  overrides: string[];
  catalog: { key: string; label: string }[];
  catalogLoading: boolean;
  onSaved: (res: UpdateOverridesResponse) => void;
}) {
  // Local draft of the override set. Reset whenever the server's
  // overrides change (after a successful save or a tenant refetch).
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(overrides),
  );
  useEffect(() => {
    setSelected(new Set(overrides));
  }, [overrides]);

  const baseline = useMemo(() => new Set(overrides), [overrides]);
  const dirty = useMemo(() => {
    if (selected.size !== baseline.size) return true;
    for (const k of selected) if (!baseline.has(k)) return true;
    return false;
  }, [selected, baseline]);

  const mutation = useMutation({
    mutationFn: (next: string[]) =>
      adminConsoleApi.updateTenantFeatureOverrides(tenantId, next),
    onSuccess: (res) => {
      onSaved(res);
      toast.success("Feature overrides updated");
    },
    onError: (err) => {
      toast.error(toApiError(err).message);
    },
  });

  function toggle(key: string, on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  }

  function save() {
    mutation.mutate(Array.from(selected));
  }

  function reset() {
    setSelected(new Set(overrides));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Feature Overrides</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Grant or remove a module for THIS tenant only, on top of its
          plan. Checking a box here forces the feature on even if the
          plan lacks it; unchecking removes the override (the plan grant,
          if any, still applies).
        </p>

        {catalogLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : catalog.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Feature catalog unavailable.
          </p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {catalog.map((entry) => {
              const checked = selected.has(entry.key);
              const inputId = `ov-${entry.key}`;
              return (
                <Label
                  key={entry.key}
                  htmlFor={inputId}
                  className="flex cursor-pointer items-center gap-3 rounded-md border border-border px-3 py-2.5 hover:bg-accent/50"
                >
                  <Checkbox
                    id={inputId}
                    checked={checked}
                    onCheckedChange={(v) =>
                      toggle(entry.key, v === true)
                    }
                    disabled={mutation.isPending}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">
                      {entry.label}
                    </span>
                    <span className="block font-mono text-xs text-muted-foreground">
                      {entry.key}
                    </span>
                  </span>
                </Label>
              );
            })}
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button onClick={save} disabled={!dirty || mutation.isPending}>
            {mutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            Save Overrides
          </Button>
          <Button
            variant="outline"
            onClick={reset}
            disabled={!dirty || mutation.isPending}
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Signup source (first touch, as the browser reported it at register) +
 * the "internal workspace" switch that keeps our own demo / QA / test
 * companies out of the signup funnel. The switch is super_admin only
 * (PUT /admin/tenants/{id}/internal); support sees the state read-only.
 */
function SignupSourceCard({
  tenantId,
  isInternal,
  attribution,
  onSaved,
}: {
  tenantId: number;
  isInternal: boolean;
  attribution: SignupAttribution | null;
  onSaved: (isInternal: boolean) => void;
}) {
  const { user } = useAuth();
  const canEdit = user?.role === "super_admin";
  const mutation = useMutation({
    mutationFn: (next: boolean) => adminConsoleApi.setTenantInternal(tenantId, next),
    onSuccess: (res) => {
      onSaved(res.is_internal);
      toast.success(
        res.is_internal
          ? "Marked as internal — left out of the signup funnel."
          : "Counted in the signup funnel again.",
      );
    },
    onError: (err) => toast.error(toApiError(err).message),
  });

  const rows: [string, string | undefined][] = attribution
    ? [
        ["Campaign source", attribution.utm_source],
        ["Medium", attribution.utm_medium],
        ["Campaign", attribution.utm_campaign],
        ["Term", attribution.utm_term],
        ["Content", attribution.utm_content],
        ["Came from", attribution.referrer_host],
        ["First page", attribution.landing_path],
        [
          "First visit",
          attribution.captured_at ? fmtDateTime(attribution.captured_at) : undefined,
        ],
      ]
    : [];
  const shown = rows.filter(([, v]) => v);

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-base">Signup Source</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {shown.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Direct — no campaign or referring site was recorded when this
            company signed up.
          </p>
        ) : (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
            {shown.map(([label, value]) => (
              <div key={label} className="min-w-0">
                <dt className="text-muted-foreground">{label}</dt>
                <dd className="truncate" title={value}>
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        )}
        <Separator />
        <div className="flex items-start justify-between gap-4">
          <div>
            <Label htmlFor="tenant-internal" className="text-sm font-medium">
              Internal Workspace
            </Label>
            <p className="text-sm text-muted-foreground">
              Our own demo, QA or test company. Internal workspaces are left
              out of the signup funnel. Nothing else changes.
            </p>
          </div>
          <Switch
            id="tenant-internal"
            checked={isInternal}
            disabled={!canEdit || mutation.isPending}
            onCheckedChange={(next) => mutation.mutate(next)}
            aria-describedby={canEdit ? undefined : "tenant-internal-ro"}
          />
        </div>
        {!canEdit && (
          <p id="tenant-internal-ro" className="text-xs text-muted-foreground">
            Only a super admin can change this.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
